<script setup lang="ts">
  import type { AiExtractDataResponse, StakeholderType } from '@gat-ship/types'
  import {
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderTitle
  } from '@gat-ship/vue-components'
  import { inject, ref } from 'vue'
  import CreateStakeholderForm from '../forms/CreateStakeholderForm.vue'
  import { text } from '@gat-ship/stakeholders'
  import {
    useCreateStakeholder,
    type ModalStakeholderCreateParams
  } from '../../../../composables/useCreateStakeholder'
  import { stakeholderStatsKey } from '@gat-ship/types/injection-keys'
  import { SmartPasteButton } from '@gat-ship/smart-paste'

  const _stakeholderType = ref<StakeholderType>()

  const { commands, getSmartPasteTarget, setAiValues } = useCreateStakeholder()
  const stakeholderStats = inject(stakeholderStatsKey, undefined)

  const isOpen = defineModel<boolean>('open', { required: false })

  const modalTitle = ref('Create Stakeholder')
  const modalDescription = ref('Create a new stakeholder')

  function showModal(params: ModalStakeholderCreateParams) {
    modalTitle.value = text.views.createNew(
      params.stakeholderType
    ).pageHeader.title
    modalDescription.value = text.views.createNew(
      params.stakeholderType
    ).pageHeader.description
    _stakeholderType.value = params.stakeholderType
    isOpen.value = true
  }

  async function onSaveClicked() {
    const result = await commands.createStakeholder()
    if (!result) {
      return
    }
    if ('newState' in result) {
      stakeholderStats?.addAddedStakeholder(result.newState)
    }
  }

  function onExtracted(data: AiExtractDataResponse) {
    setAiValues(data)
  }

  function closeModal() {
    isOpen.value = false
  }

  defineExpose({
    showModal,
    closeModal
  })
</script>
<template>
  <modal v-model:open="isOpen" class="w-full max-w-xl">
    <ModalHeader>
      <ModalHeaderTitle>
        {{ modalTitle }}
        <div class="ml-auto flex items-center gap-2">
          <SmartPasteButton
            v-if="_stakeholderType"
            class="w-fit"
            prompt-type="data-extraction"
            label="Smart Paste"
            enable-keyboard-shortcut
            :target="getSmartPasteTarget(_stakeholderType)"
            @extracted="onExtracted"
          />
        </div>
      </ModalHeaderTitle>
      <ModalHeaderDescription>
        {{ modalDescription }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent class="@container flex flex-col gap-4">
      <CreateStakeholderForm
        v-if="_stakeholderType"
        :stakeholder-type="_stakeholderType"
      />
    </ModalContent>
    <ModalFooter>
      <Button @click="onSaveClicked">Save Changes</Button>
      <Button variant="ghost" @click="closeModal"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
